import { useTranslation } from '@/shared/web/i18n/LanguageProvider';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/web/components/AuthLayout';
import { useAuth } from './ux/hooks/useAuth';
import { useLoginError } from './ux/hooks/useLoginError';
import { GoogleSignInButton } from './ux/components/GoogleSignInButton';
import { Alert, AlertDescription } from '@/web/components/ui/alert';
import { AlertCircle } from 'lucide-react';

/**
 * /login — the only way into the CRM.
 *
 * There is no registration form by design: the allowlist lives on the server
 * and a Google account on the agency domain *is* the credential.
 */
export default function LoginScreen() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const { message } = useLoginError();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pair = /^[0-9a-f-]{36}$/i.test(params.get('extensionPair') || '') ? params.get('extensionPair') : null;

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate(pair ? `/chrome-extension#pair=${pair}` : '/pipeline', { replace: true });
  }, [isAuthenticated, isLoading, navigate, pair]);

  return (
    <AuthLayout audience="employee">
        <div className="border-border bg-card squircle-lg flex flex-col gap-5 border p-6">
          <div className="flex flex-col gap-1.5 text-center">
            <h1 className="text-xl font-semibold tracking-tight">{t("Sign in to the CRM")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("Influencer outreach, contracts and creator relationships.")}
            </p>
          </div>

          {message && (
            <Alert variant="destructive" className="squircle">
              <AlertCircle />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <GoogleSignInButton onClick={() => pair ? window.location.assign(`/auth/google?extensionPair=${pair}`) : signIn()} disabled={isLoading} />

          <p className="text-muted-foreground text-center text-xs leading-relaxed">
            Restricted to <span className="text-foreground font-medium">@a2.agency</span> accounts.
            We ask for permission to send email so outreach goes out from your own mailbox.
          </p>
        </div>
    </AuthLayout>
  );
}
